import { useCallback, useEffect, useRef, useState } from 'react';
import { useToast } from '../contexts/ToastContext';
import { useTranslation } from '../i18n';

/**
 * The moment a goal or sub goal is completed (T-103, same as native T-098): a success toast
 * with Undo. Call it only after the save succeeded; the undo failure is reported by the
 * caller's own error handling, so it is swallowed here.
 */
export const useCompletionToast = () => {
  const toast = useToast();
  const { t } = useTranslation();
  return useCallback((message: string, undo: () => unknown) => {
    toast.success(message, {
      actions: [{
        label: t('common.undo'),
        onClick: () => { Promise.resolve().then(undo).catch(() => undefined); },
      }],
    });
  }, [toast, t]);
};

const POP_MS = 520;

const prefersReducedMotion = (): boolean =>
  typeof window !== 'undefined' && Boolean(window.matchMedia?.('(prefers-reduced-motion: reduce)').matches);

/**
 * True for a short moment when `active` turns from false to true (a list reaching 100%),
 * for a scale pop. Not on the first render, and never when the user asked for less motion.
 */
export const useCelebrationPop = (active: boolean): boolean => {
  const previous = useRef(active);
  const [popping, setPopping] = useState(false);

  useEffect(() => {
    const turnedOn = active && !previous.current;
    previous.current = active;
    if (!turnedOn || prefersReducedMotion()) return undefined;
    setPopping(true);
    const timer = window.setTimeout(() => setPopping(false), POP_MS);
    return () => {
      window.clearTimeout(timer);
      setPopping(false);
    };
  }, [active]);

  return popping;
};
